import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Tawk.to live chat widget.
 *
 * Replace these with the two IDs from your Tawk.to dashboard:
 * Administration > Chat Widget > the embed URL
 *   https://embed.tawk.to/<PROPERTY_ID>/<WIDGET_ID>
 *
 * These are public embed IDs (safe in client code, not secrets).
 */
const TAWK_PROPERTY_ID = "6a70fe3d6299c31d47e71962";
const TAWK_WIDGET_ID = "1jv4m28bn";

/** Routes where the launcher should stay hidden. */
const HIDDEN_ROUTES = ["/cart", "/checkout-success", "/checkout-canceled"];

declare global {
  interface Window {
    Tawk_API?: {
      showWidget?: () => void;
      hideWidget?: () => void;
      onLoad?: () => void;
      customStyle?: unknown;
      [key: string]: unknown;
    };
    Tawk_LoadStart?: Date;
  }
}

const SCRIPT_ID = "tawk-to-embed";

function loadTawkScript() {
  if (document.getElementById(SCRIPT_ID)) return;

  window.Tawk_API = window.Tawk_API || {};
  window.Tawk_LoadStart = new Date();

  // Lift the launcher so it clears the mobile footer / sticky UI.
  window.Tawk_API.customStyle = {
    visibility: {
      desktop: { position: "br", xOffset: 20, yOffset: 20 },
      mobile: { position: "br", xOffset: 12, yOffset: 72 },
    },
  };

  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://embed.tawk.to/${TAWK_PROPERTY_ID}/${TAWK_WIDGET_ID}`;
  script.charset = "UTF-8";
  script.setAttribute("crossorigin", "*");
  document.body.appendChild(script);
}

/** Defer loading until the browser is idle so it never blocks first paint. */
function whenIdle(callback: () => void) {
  if (typeof window === "undefined") return () => {};

  const ric = (window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  }).requestIdleCallback;

  if (ric) {
    const handle = ric(callback, { timeout: 4000 });
    return () => window.cancelIdleCallback?.(handle);
  }

  const timer = window.setTimeout(callback, 2500);
  return () => window.clearTimeout(timer);
}

export function TawkChat() {
  const { pathname } = useLocation();
  const loadedRef = useRef(false);

  const hidden = HIDDEN_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  );

  // Load the script once, lazily.
  useEffect(() => {
    if (!TAWK_PROPERTY_ID || loadedRef.current) return;
    loadedRef.current = true;
    return whenIdle(loadTawkScript);
  }, []);

  // Show/hide on route changes without reloading the widget.
  useEffect(() => {
    if (!TAWK_PROPERTY_ID) return;

    const apply = () => {
      const api = window.Tawk_API;
      if (!api) return false;
      if (hidden) {
        api.hideWidget?.();
      } else {
        api.showWidget?.();
      }
      return true;
    };

    if (apply()) return;

    // Widget may not be ready yet — retry briefly until it is.
    const interval = window.setInterval(() => {
      if (apply()) window.clearInterval(interval);
    }, 400);
    const stop = window.setTimeout(() => window.clearInterval(interval), 15000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(stop);
    };
  }, [hidden]);

  return null;
}

export default TawkChat;
